import React, { useState } from 'react';
import { ExpenseEntry } from '../types';

interface ExpenseSummaryProps {
  expenses: ExpenseEntry[];
}

interface CategorySummary {
  category: string;
  totalAmount: number;
  count: number;
  percentage: number;
}

interface ExpenseCOGSSummary {
  type: string;
  totalAmount: number;
  count: number;
  percentage: number;
}

interface MerchantSummary {
  merchant: string;
  totalAmount: number;
  category: string;
  expenseCOGS: string;
  count: number;
}

interface MonthSummary {
  month: string;
  totalAmount: number;
  expenseAmount: number;
  cogsAmount: number;
  expenseCount: number;
}

const ExpenseSummary: React.FC<ExpenseSummaryProps> = ({ expenses }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'merchants' | 'monthly' | 'expenseCOGS'>('overview');
  
  if (expenses.length === 0) {
    return null;
  }

  // Calculate summary by category
  const categorySummaries: CategorySummary[] = [];
  const categoryMap: Record<string, { totalAmount: number; count: number }> = {};
  
  // Calculate summary by expense/COGS
  const expenseCOGSSummaries: ExpenseCOGSSummary[] = [];
  const expenseCOGSMap: Record<string, { totalAmount: number; count: number }> = {};
  
  let grandTotal = 0;

  // First, calculate totals
  expenses.forEach((expense) => {
    const amount = expense.amount;
    grandTotal += amount;

    // Category calculations
    if (!categoryMap[expense.category]) {
      categoryMap[expense.category] = { totalAmount: 0, count: 0 };
    }
    categoryMap[expense.category].totalAmount += amount;
    categoryMap[expense.category].count += 1;
    
    // Expense/COGS calculations
    if (!expenseCOGSMap[expense.expenseCOGS]) {
      expenseCOGSMap[expense.expenseCOGS] = { totalAmount: 0, count: 0 };
    }
    expenseCOGSMap[expense.expenseCOGS].totalAmount += amount;
    expenseCOGSMap[expense.expenseCOGS].count += 1;
  });

  // Then, calculate percentages and build summaries
  for (const [category, data] of Object.entries(categoryMap)) {
    categorySummaries.push({
      category,
      totalAmount: data.totalAmount,
      count: data.count,
      percentage: (data.totalAmount / grandTotal) * 100
    });
  }

  // Sort by amount descending
  categorySummaries.sort((a, b) => b.totalAmount - a.totalAmount);
  
  // Do the same for expense/COGS
  for (const [type, data] of Object.entries(expenseCOGSMap)) {
    expenseCOGSSummaries.push({
      type,
      totalAmount: data.totalAmount,
      count: data.count,
      percentage: (data.totalAmount / grandTotal) * 100
    });
  }
  
  // Sort by amount descending
  expenseCOGSSummaries.sort((a, b) => b.totalAmount - a.totalAmount);

  // Calculate summary by merchant
  const merchantSummaries: MerchantSummary[] = [];
  const merchantMap: Record<string, { 
    totalAmount: number; 
    category: string; 
    expenseCOGS: string;
    count: number;
    transactions: ExpenseEntry[];
  }> = {};

  expenses.forEach((expense) => {
    const merchant = extractMerchantName(expense.description);
    
    if (!merchantMap[merchant]) {
      merchantMap[merchant] = { 
        totalAmount: 0, 
        category: expense.category, 
        expenseCOGS: expense.expenseCOGS,
        count: 0,
        transactions: []
      };
    }
    
    merchantMap[merchant].totalAmount += expense.amount;
    merchantMap[merchant].count += 1;
    merchantMap[merchant].transactions.push(expense);
  });

  // For each merchant, find the most common category
  for (const [merchant, data] of Object.entries(merchantMap)) {
    // Find most common category for this merchant
    const categoryCount: Record<string, number> = {};
    const expenseCOGSCount: Record<string, number> = {};
    
    data.transactions.forEach(trans => {
      categoryCount[trans.category] = (categoryCount[trans.category] || 0) + 1;
      expenseCOGSCount[trans.expenseCOGS] = (expenseCOGSCount[trans.expenseCOGS] || 0) + 1;
    });
    
    let mostCommonCategory = data.category;
    let mostCommonCategoryCount = 0;
    
    for (const [cat, count] of Object.entries(categoryCount)) {
      if (count > mostCommonCategoryCount) {
        mostCommonCategory = cat;
        mostCommonCategoryCount = count;
      }
    }
    
    let mostCommonExpenseCOGS = data.expenseCOGS;
    let mostCommonExpenseCOGSCount = 0;
    
    for (const [type, count] of Object.entries(expenseCOGSCount)) {
      if (count > mostCommonExpenseCOGSCount) {
        mostCommonExpenseCOGS = type;
        mostCommonExpenseCOGSCount = count;
      }
    }
    
    merchantSummaries.push({
      merchant,
      totalAmount: data.totalAmount,
      category: mostCommonCategory,
      expenseCOGS: mostCommonExpenseCOGS,
      count: data.count
    });
  }

  // Sort by amount descending
  merchantSummaries.sort((a, b) => b.totalAmount - a.totalAmount);

  // Calculate summary by month
  const monthSummaries: MonthSummary[] = [];
  const monthMap: Record<string, { 
    totalAmount: number; 
    expenseAmount: number;
    cogsAmount: number;
    expenseCount: number;
  }> = {};

  expenses.forEach((expense) => {
    // Extract month from date (assuming date format is MM/DD/YYYY)
    const dateParts = expense.date.split('/');
    if (dateParts.length >= 2) {
      const monthYear = `${dateParts[0]}/${dateParts[2]}`;
      
      if (!monthMap[monthYear]) {
        monthMap[monthYear] = { 
          totalAmount: 0, 
          expenseAmount: 0,
          cogsAmount: 0,
          expenseCount: 0 
        };
      }
      
      monthMap[monthYear].totalAmount += expense.amount;
      monthMap[monthYear].expenseCount += 1;
      
      // Track Expense vs COGS amounts separately
      if (expense.expenseCOGS === 'Expense') {
        monthMap[monthYear].expenseAmount += expense.amount;
      } else if (expense.expenseCOGS === 'COGS') {
        monthMap[monthYear].cogsAmount += expense.amount;
      }
    }
  });

  for (const [month, data] of Object.entries(monthMap)) {
    monthSummaries.push({
      month,
      totalAmount: data.totalAmount,
      expenseAmount: data.expenseAmount,
      cogsAmount: data.cogsAmount,
      expenseCount: data.expenseCount
    });
  }

  // Sort by month (ascending)
  monthSummaries.sort((a, b) => {
    const [aMonth, aYear] = a.month.split('/');
    const [bMonth, bYear] = b.month.split('/');
    
    if (aYear === bYear) {
      return parseInt(aMonth) - parseInt(bMonth);
    }
    
    return parseInt(aYear) - parseInt(bYear);
  });

  function extractMerchantName(description: string): string {
    // Clean up description to extract merchant name
    let merchant = description;
    
    // Handle common patterns in credit card statements
    if (description.includes('*')) {
      const parts = description.split('*');
      if (parts.length > 1) {
        merchant = parts[1].trim();
        // Remove numbers and common transaction suffixes
        merchant = merchant.replace(/\d+/g, '').trim();
      }
    }
    
    // Remove common prefixes
    merchant = merchant
      .replace(/^PURCHASE /, '')
      .replace(/^PAYMENT /, '')
      .replace(/^PMT /, '')
      .replace(/^SQ \*/g, '')
      .replace(/^TST\*/g, '');
    
    // Remove common suffixes and transaction IDs
    merchant = merchant
      .replace(/ [A-Z0-9]{6,}$/i, '')
      .replace(/ XXXXXXXXXXXX[0-9]{4}$/i, '')
      .replace(/ XXXXXX[0-9]{4}$/i, '');
    
    // Take first word or limit to first few words for consistency
    const words = merchant.split(' ');
    if (words.length > 3) {
      merchant = words.slice(0, 3).join(' ');
    }
    
    return merchant.trim() || 'Unknown Merchant';
  }

  return (
    <div className="mt-8">
      <div className="sm:hidden">
        <select
          id="tabs"
          name="tabs"
          className="block w-full rounded-md border-gray-300 focus:border-blue-500 focus:ring-blue-500"
          value={activeTab}
          onChange={(e) => setActiveTab(e.target.value as any)}
        >
          <option value="overview">Overview by Category</option>
          <option value="expenseCOGS">Expense vs. COGS</option>
          <option value="merchants">Top Merchants</option>
          <option value="monthly">Monthly Breakdown</option>
        </select>
      </div>
      <div className="hidden sm:block">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('overview')}
              className={`${
                activeTab === 'overview'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Overview by Category
            </button>
            <button
              onClick={() => setActiveTab('expenseCOGS')}
              className={`${
                activeTab === 'expenseCOGS'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Expense vs. COGS
            </button>
            <button
              onClick={() => setActiveTab('merchants')}
              className={`${
                activeTab === 'merchants'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Top Merchants
            </button>
            <button
              onClick={() => setActiveTab('monthly')}
              className={`${
                activeTab === 'monthly'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Monthly Breakdown
            </button>
          </nav>
        </div>
      </div>

      <div className="mt-4">
        {activeTab === 'overview' && (
          <div>
            <h3 className="text-lg font-medium text-gray-900">Expenses by Category</h3>
            <p className="text-sm text-gray-500">Total: ${grandTotal.toFixed(2)}</p>
            
            <div className="mt-4 flex flex-col">
              <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead>
                      <tr>
                        <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 md:pl-0">
                          Category
                        </th>
                        <th scope="col" className="py-3.5 px-3 text-right text-sm font-semibold text-gray-900">
                          Amount
                        </th>
                        <th scope="col" className="py-3.5 px-3 text-right text-sm font-semibold text-gray-900">
                          Count
                        </th>
                        <th scope="col" className="py-3.5 px-3 text-right text-sm font-semibold text-gray-900">
                          % of Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {categorySummaries.map((summary) => (
                        <tr key={summary.category}>
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6 md:pl-0">
                            {summary.category}
                          </td>
                          <td className="whitespace-nowrap py-4 px-3 text-sm text-right text-gray-500">
                            ${summary.totalAmount.toFixed(2)}
                          </td>
                          <td className="whitespace-nowrap py-4 px-3 text-sm text-right text-gray-500">
                            {summary.count}
                          </td>
                          <td className="whitespace-nowrap py-4 px-3 text-sm text-right text-gray-500">
                            {summary.percentage.toFixed(1)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            
            <div className="mt-6 h-64">
              <div className="h-full flex">
                <div className="flex-1 flex items-end">
                  {categorySummaries.map((summary, index) => (
                    <div 
                      key={summary.category}
                      className="w-16 mx-1 relative" 
                      style={{ height: `${summary.percentage}%`, maxHeight: '100%' }}
                    >
                      <div 
                        className="absolute inset-0 w-full"
                        style={{ backgroundColor: getColorForCategory(summary.category) }}
                      ></div>
                      <div className="absolute inset-x-0 bottom-0 -mb-6 transform -rotate-45 origin-left">
                        <span className="text-xs font-medium truncate">
                          {summary.category}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'expenseCOGS' && (
          <div>
            <h3 className="text-lg font-medium text-gray-900">Expenses vs. COGS Breakdown</h3>
            <p className="text-sm text-gray-500">Total: ${grandTotal.toFixed(2)}</p>
            
            <div className="mt-4 flex flex-col">
              <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead>
                      <tr>
                        <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 md:pl-0">
                          Type
                        </th>
                        <th scope="col" className="py-3.5 px-3 text-right text-sm font-semibold text-gray-900">
                          Amount
                        </th>
                        <th scope="col" className="py-3.5 px-3 text-right text-sm font-semibold text-gray-900">
                          Count
                        </th>
                        <th scope="col" className="py-3.5 px-3 text-right text-sm font-semibold text-gray-900">
                          % of Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {expenseCOGSSummaries.map((summary) => (
                        <tr key={summary.type}>
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6 md:pl-0">
                            {summary.type}
                          </td>
                          <td className="whitespace-nowrap py-4 px-3 text-sm text-right text-gray-500">
                            ${summary.totalAmount.toFixed(2)}
                          </td>
                          <td className="whitespace-nowrap py-4 px-3 text-sm text-right text-gray-500">
                            {summary.count}
                          </td>
                          <td className="whitespace-nowrap py-4 px-3 text-sm text-right text-gray-500">
                            {summary.percentage.toFixed(1)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            
            <div className="mt-6 h-64">
              <div className="h-full flex justify-center">
                <div className="w-64 h-64">
                  {/* Simple pie chart */}
                  <div className="relative w-full h-full rounded-full overflow-hidden">
                    {expenseCOGSSummaries.map((summary, index, array) => {
                      // Calculate the start angle based on previous segments
                      const previousPercentageTotal = array
                        .slice(0, index)
                        .reduce((total, curr) => total + curr.percentage, 0);
                      
                      const startAngle = (previousPercentageTotal / 100) * 360;
                      const angle = (summary.percentage / 100) * 360;
                      
                      return (
                        <div
                          key={summary.type}
                          className="absolute inset-0"
                          style={{
                            background: summary.type === 'Expense' ? '#3b82f6' : '#10b981',
                            clipPath: `conic-gradient(from ${startAngle}deg, 
                                      transparent 0deg, 
                                      currentColor ${angle}deg, 
                                      transparent ${angle}deg)`
                          }}
                        ></div>
                      );
                    })}
                    {/* Center circle for donut chart effect */}
                    <div className="absolute inset-1/4 bg-white rounded-full flex items-center justify-center">
                      <div className="text-center">
                        <div className="text-xs font-medium text-gray-500">Total</div>
                        <div className="text-lg font-semibold">${grandTotal.toFixed(0)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-4 flex justify-center">
                {expenseCOGSSummaries.map((summary) => (
                  <div key={summary.type} className="flex items-center mx-4">
                    <div 
                      className="w-4 h-4 mr-1 rounded-sm" 
                      style={{ backgroundColor: summary.type === 'Expense' ? '#3b82f6' : '#10b981' }}
                    ></div>
                    <span className="text-sm">
                      {summary.type}: ${summary.totalAmount.toFixed(0)} ({summary.percentage.toFixed(1)}%)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'merchants' && (
          <div>
            <h3 className="text-lg font-medium text-gray-900">Top Merchants</h3>
            <p className="text-sm text-gray-500">
              Showing top {Math.min(10, merchantSummaries.length)} merchants by spend
            </p>
            
            <div className="mt-4 flex flex-col">
              <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead>
                      <tr>
                        <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 md:pl-0">
                          Merchant
                        </th>
                        <th scope="col" className="py-3.5 px-3 text-right text-sm font-semibold text-gray-900">
                          Amount
                        </th>
                        <th scope="col" className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                          Category
                        </th>
                        <th scope="col" className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                          Type
                        </th>
                        <th scope="col" className="py-3.5 px-3 text-right text-sm font-semibold text-gray-900">
                          # Transactions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {merchantSummaries.slice(0, 10).map((summary) => (
                        <tr key={summary.merchant}>
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6 md:pl-0">
                            {summary.merchant}
                          </td>
                          <td className="whitespace-nowrap py-4 px-3 text-sm text-right text-gray-500">
                            ${summary.totalAmount.toFixed(2)}
                          </td>
                          <td className="whitespace-nowrap py-4 px-3 text-sm text-gray-500">
                            {summary.category}
                          </td>
                          <td className="whitespace-nowrap py-4 px-3 text-sm text-gray-500">
                            {summary.expenseCOGS}
                          </td>
                          <td className="whitespace-nowrap py-4 px-3 text-sm text-right text-gray-500">
                            {summary.count}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'monthly' && (
          <div>
            <h3 className="text-lg font-medium text-gray-900">Monthly Expense Breakdown</h3>
            <p className="text-sm text-gray-500">
              Total across {monthSummaries.length} months: ${grandTotal.toFixed(2)}
            </p>
            
            <div className="mt-4 flex flex-col">
              <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead>
                      <tr>
                        <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 md:pl-0">
                          Month
                        </th>
                        <th scope="col" className="py-3.5 px-3 text-right text-sm font-semibold text-gray-900">
                          Total
                        </th>
                        <th scope="col" className="py-3.5 px-3 text-right text-sm font-semibold text-gray-900">
                          Expenses
                        </th>
                        <th scope="col" className="py-3.5 px-3 text-right text-sm font-semibold text-gray-900">
                          COGS
                        </th>
                        <th scope="col" className="py-3.5 px-3 text-right text-sm font-semibold text-gray-900">
                          # Transactions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {monthSummaries.map((summary) => (
                        <tr key={summary.month}>
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6 md:pl-0">
                            {formatMonth(summary.month)}
                          </td>
                          <td className="whitespace-nowrap py-4 px-3 text-sm text-right text-gray-500">
                            ${summary.totalAmount.toFixed(2)}
                          </td>
                          <td className="whitespace-nowrap py-4 px-3 text-sm text-right text-gray-500">
                            ${summary.expenseAmount.toFixed(2)}
                          </td>
                          <td className="whitespace-nowrap py-4 px-3 text-sm text-right text-gray-500">
                            ${summary.cogsAmount.toFixed(2)}
                          </td>
                          <td className="whitespace-nowrap py-4 px-3 text-sm text-right text-gray-500">
                            {summary.expenseCount}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            
            <div className="mt-6 h-64">
              <div className="h-full flex">
                <div className="flex-1 flex items-end">
                  {monthSummaries.map((summary) => (
                    <div 
                      key={summary.month}
                      className="flex-1 mx-1 relative" 
                      style={{ 
                        maxWidth: `${100 / Math.min(monthSummaries.length, 12)}%`,
                        height: `${(summary.totalAmount / Math.max(...monthSummaries.map(s => s.totalAmount))) * 100}%` 
                      }}
                    >
                      {/* COGS portion of the bar */}
                      <div 
                        className="absolute inset-x-0 bottom-0 w-full"
                        style={{ 
                          backgroundColor: '#10b981',  // green
                          height: `${(summary.cogsAmount / summary.totalAmount) * 100}%` 
                        }}
                      ></div>
                      {/* Expense portion of the bar */}
                      <div 
                        className="absolute inset-x-0 bottom-0 w-full"
                        style={{ 
                          backgroundColor: '#3b82f6',  // blue
                          height: `${(summary.expenseAmount / summary.totalAmount) * 100}%`,
                          bottom: `${(summary.cogsAmount / summary.totalAmount) * 100}%`
                        }}
                      ></div>
                      <div className="absolute inset-x-0 bottom-0 -mb-6 transform -rotate-45 origin-left">
                        <span className="text-xs font-medium truncate">
                          {formatMonth(summary.month, true)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="mt-8 flex justify-center">
              <div className="flex items-center mx-4">
                <div className="w-4 h-4 mr-1 rounded-sm bg-blue-500"></div>
                <span className="text-sm">Expenses</span>
              </div>
              <div className="flex items-center mx-4">
                <div className="w-4 h-4 mr-1 rounded-sm bg-green-500"></div>
                <span className="text-sm">COGS</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

function formatMonth(monthStr: string, short: boolean = false): string {
  const [month, year] = monthStr.split('/');
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  const shortMonthNames = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];
  
  const monthIndex = parseInt(month) - 1;
  
  if (short) {
    return `${shortMonthNames[monthIndex]} ${year}`;
  }
  
  return `${monthNames[monthIndex]} ${year}`;
}

function getColorForCategory(category: string): string {
  // Map of category to color
  const categoryColors: Record<string, string> = {
    'Advertising': '#ef4444', // red
    'Meals and Entertainment': '#f97316', // orange
    'Office Expenses': '#eab308', // yellow
    'Professional Services': '#22c55e', // green
    'Rent and Utilities': '#3b82f6', // blue
    'SaaS and Software': '#a855f7', // purple
    'Travel': '#ec4899', // pink
    'Uncategorized': '#6b7280' // gray
  };
  
  return categoryColors[category] || '#6b7280';
}

export default ExpenseSummary; 